export const BLOCK_TYPE_CATALOG = [
  'question_frame',
  'audience_note',
  'short_answer',
  'long_explanation',
  'scholar_view',
  'historical_note',
  'ayah_block',
  'hadith_block',
  'criticism_block',
  'response_block',
  'compare_block',
  'reflection_prompt',
  'next_step_card',
  'callout_warning',
  'glossary_term',
  'quote_block',
  'faq_item',
  'books_body',
  'heading',
  'subheading',
  'paragraph',
  'list',
  'ordered_list',
  'checklist',
  'image',
  'gallery',
  'carousel',
  'video',
  'audio',
  'table',
  'code',
  'divider',
  'toggle',
  'timeline',
  'stat',
  'reference_list',
  'argument_claim',
  'argument_evidence',
  'argument_counter',
  'argument_rebuttal',
  'qa_pair',
  'poll',
  'annotation',
  'note',
  'warning',
  'info',
  'success',
  'danger',
  'cta',
  'resource_link',
  'resource_list',
  'book_reference',
  'chapter_reference',
  'person_profile',
  'term_definition',
  'comparison_table',
  'pros_cons',
  'myth_fact',
  'timeline_event',
  'chronology_group',
  'map_embed',
  'diagram_embed',
  'chart_embed',
  'equation',
  'footnote',
  'citation',
  'bibliography',
  'sidenote',
  'pull_quote',
  'hero',
  'kicker',
  'summary',
  'learning_objectives',
  'key_takeaways',
  'recap',
  'step',
  'step_group',
  'path_branch',
  'branch_option',
  'exercise',
  'prompt',
  'discussion_question',
  'case_study',
  'scenario',
  'example',
  'example_group',
  'do_dont',
  'principle',
  'rule',
  'exception',
  'definition_list',
  'matrix',
  'rubric',
  'decision_tree',
  'flowchart',
  'metadata',
  'author_note',
  'editor_note',
  'transcript',
  'transliteration',
  'translation',
  'arabic_text',
  'verse_group',
  'hadith_group',
  'source_card',
  'media_embed',
  'interactive',
  'badge_row',
  'chips_row',
  'unknown',
] as const;

export type CatalogBlockType = (typeof BLOCK_TYPE_CATALOG)[number];

const CATALOG_SET = new Set<string>(BLOCK_TYPE_CATALOG);

export function isCatalogBlockType(value: string): value is CatalogBlockType {
  return CATALOG_SET.has(value);
}

export const BLOCK_TYPE_ALIASES: Record<string, CatalogBlockType> = {
  framing: 'question_frame',
  frame: 'question_frame',
  persona_note: 'audience_note',
  concise_answer: 'short_answer',
  explanation: 'long_explanation',
  deep_dive: 'long_explanation',
  scholarly_view: 'scholar_view',
  history_note: 'historical_note',
  quran_quote: 'ayah_block',
  hadith_quote: 'hadith_block',
  objection: 'criticism_block',
  rebuttal: 'response_block',
  comparison: 'compare_block',
  reflection: 'reflection_prompt',
  next_steps: 'next_step_card',
  caution: 'callout_warning',
  glossary: 'glossary_term',
  quote: 'quote_block',
  testimonial: 'quote_block',
  faq: 'faq_item',
  rich_body: 'books_body',
  text: 'paragraph',
  body: 'paragraph',
  markdown: 'paragraph',
  md: 'paragraph',
  heading_1: 'heading',
  h1: 'heading',
  title: 'heading',
  heading_2: 'subheading',
  heading_3: 'subheading',
  h2: 'subheading',
  h3: 'subheading',
  bullets: 'list',
  bullet_list: 'list',
  bulleted_list: 'list',
  numbered_list: 'ordered_list',
  steps: 'ordered_list',
  task_list: 'checklist',
  hero_image: 'image',
  figure: 'image',
  video_embed: 'video',
  embed_video: 'video',
  audio_clip: 'audio',
  podcast: 'audio',
  data_table: 'table',
  code_block: 'code',
  snippet: 'code',
  separator: 'divider',
  details: 'toggle',
  accordion: 'toggle',
  chronology: 'timeline',
  metric: 'stat',
  key_fact: 'stat',
  references: 'reference_list',
  bibliography_list: 'reference_list',
  paragraph_block: 'paragraph',
  rich_text: 'paragraph',
};

export function normalizeCatalogBlockType(raw: string): CatalogBlockType {
  const key = raw.trim().toLowerCase();
  if (!key) return 'unknown';
  const aliased = BLOCK_TYPE_ALIASES[key];
  if (aliased) return aliased;
  return isCatalogBlockType(key) ? key : 'unknown';
}

export type BlockTemplateKind =
  | 'narrative'
  | 'support'
  | 'citation'
  | 'media'
  | 'interactive'
  | 'structure'
  | 'fallback';

export function templateForBlockType(type: CatalogBlockType): BlockTemplateKind {
  if (
    [
      'question_frame',
      'short_answer',
      'long_explanation',
      'paragraph',
      'heading',
      'subheading',
      'summary',
      'example',
      'scenario',
    ].includes(type)
  ) {
    return 'narrative';
  }
  if (
    [
      'ayah_block',
      'hadith_block',
      'glossary_term',
      'historical_note',
      'scholar_view',
      'reference_list',
      'citation',
      'footnote',
      'source_card',
      'translation',
      'transliteration',
      'arabic_text',
      'verse_group',
      'hadith_group',
    ].includes(type)
  ) {
    return 'citation';
  }
  if (
    [
      'image',
      'gallery',
      'carousel',
      'video',
      'audio',
      'media_embed',
      'map_embed',
      'diagram_embed',
      'chart_embed',
    ].includes(type)
  ) {
    return 'media';
  }
  if (
    [
      'toggle',
      'poll',
      'interactive',
      'exercise',
      'prompt',
      'discussion_question',
      'qa_pair',
      'decision_tree',
      'flowchart',
    ].includes(type)
  ) {
    return 'interactive';
  }
  if (
    [
      'table',
      'comparison_table',
      'pros_cons',
      'myth_fact',
      'timeline',
      'timeline_event',
      'chronology_group',
      'matrix',
      'rubric',
      'list',
      'ordered_list',
      'checklist',
      'definition_list',
      'step',
      'step_group',
      'path_branch',
      'branch_option',
      'key_takeaways',
      'learning_objectives',
      'recap',
    ].includes(type)
  ) {
    return 'structure';
  }
  if (['books_body', 'next_step_card', 'callout_warning', 'quote_block', 'faq_item', 'criticism_block', 'response_block'].includes(type)) {
    return 'support';
  }
  return 'fallback';
}
